import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { theme as mainTheme } from "../styles/theme";
import { BREAKPOINT_MOBILE } from "../constants";

export type BreakpointKey = keyof Theme["breakpoints"]["values"];
export type StyleObject = Record<string, any> | undefined;
export type DeviceStylesInput =
  | { mobile: StyleObject }
  | { desktop: StyleObject };

/**
 * Generates responsive styles based on:
 * - mobile-first or desktop-first mode
 * - a list of fallback styles
 * - a chosen mobile breakpoint
 *
 * Works with any custom breakpoint configuration in the theme.
 */
export function deviceStyles(
  first: DeviceStylesInput,
  styles: StyleObject[] = [],
  mobileBreakpoint: BreakpointKey = BREAKPOINT_MOBILE,
  theme: Theme = mainTheme
) {
  const isMobileFirst = "mobile" in first;
  const firstStyles = isMobileFirst ? first.mobile : first.desktop;

  const bpKeys = Object.keys(theme.breakpoints.values) as BreakpointKey[];
  const output: Record<string, any> = {};

  const applyUp = (bp: BreakpointKey, style: StyleObject) => {
    output[theme.breakpoints.up(bp)] = style;
  };

  const applyBetween = (
    start: BreakpointKey,
    end: BreakpointKey,
    style: StyleObject
  ) => {
    output[theme.breakpoints.between(start, end)] = style;
  };

  // ----------------------------------------------------------------------
  // MOBILE-FIRST
  // ----------------------------------------------------------------------
  if (isMobileFirst) {
    const mobileIndex = bpKeys.indexOf(mobileBreakpoint);
    if (mobileIndex === -1) {
      console.warn(`deviceStyles: unknown mobile breakpoint "${mobileBreakpoint}"`);
      return output;
    }

    // 1) Mobile styles: from xs up to mobileBreakpoint (exclusive)
    applyBetween("xs", mobileBreakpoint, firstStyles);

    // 2) Desktop styles: start at mobileBreakpoint and follow breakpoints in order.
    //    styles[0] => mobileBreakpoint
    //    styles[1] => next breakpoint after mobileBreakpoint, etc.
    const desktopBps = bpKeys.slice(mobileIndex); // [mobileBp, next, next, ...]

    for (let i = 0; i < desktopBps.length && i < styles.length; i++) {
      const style = styles[i];
      if (!style) continue; // skip breakpoint if style is undefined
      const bp = desktopBps[i];
      applyUp(bp, style);
    }

    return output;
  }

  // ----------------------------------------------------------------------
  // DESKTOP-FIRST
  // ----------------------------------------------------------------------
  const mobileIndex = bpKeys.indexOf(mobileBreakpoint);
  if (mobileIndex === -1) {
    console.warn(`deviceStyles: unknown mobile breakpoint "${mobileBreakpoint}"`);
    return output;
  }

  // Breakpoints before the mobile breakpoint
  const desktopBpsBeforeMobile = bpKeys.slice(0, mobileIndex);

  // styles[0] => first breakpoint (xs)
  // styles[1] => next (sm), etc.
  for (let i = 0; i < desktopBpsBeforeMobile.length && i < styles.length; i++) {
    const style = styles[i];
    if (!style) continue; // skip if undefined
    const bp = desktopBpsBeforeMobile[i];
    applyUp(bp, style);
  }

  // At and above mobileBreakpoint: apply the desktop base style
  applyUp(mobileBreakpoint, firstStyles);

  return output;
}

/**
 * Hook version of deviceStyles().
 * Automatically uses the theme from useTheme().
 */
export function useDeviceStyles() {
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down(BREAKPOINT_MOBILE));

  return {
    isMobileDevice,
    deviceStyles,
  };
}
